import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { serviceDetails } from '../data/serviceDetails';
import { ROUTES, getServiceDetailRoute } from '../constants/routes';

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const services = Object.values(serviceDetails);

  return (
    <header className={`glass-nav ${scrolled ? 'sticky-active' : ''}`} style={{
      position: 'fixed', top: 0, width: '100%', zIndex: 1000,
      transition: 'background 0.35s ease, box-shadow 0.35s ease',
      padding: '1.25rem 0',
      background: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.85)',
      boxShadow: scrolled ? '0 10px 30px rgba(0,0,0,0.05)' : 'none',
      borderBottom: '1px solid rgba(0,0,0,0.05)',
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)'
    }}>
      <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to={ROUTES.HOME} className="flex items-center gap-2" style={{ zIndex: 1100, textDecoration: 'none' }}>
          <div style={{ width: '40px', height: '40px', background: 'var(--primary)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
            <i className="fa-solid fa-microchip" style={{ fontSize: '1.2rem' }}></i>
          </div>
          <span style={{ fontSize: '1.4rem', fontWeight: 800, letterSpacing: '-0.5px', color: 'var(--primary)' }}>
            Tendo<span className="text-gradient">Tec</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hide-mobile" style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
          <Link to={ROUTES.HOME} className={location.pathname === ROUTES.HOME ? 'nav-active' : 'nav-link'}>Startseite</Link>
          
          <div className="nav-dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
            <button 
              onMouseEnter={() => setDropdownOpen(true)}
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`nav-link flex items-center gap-2 ${location.pathname.startsWith(`${ROUTES.SERVICES}/`) || location.pathname === ROUTES.SERVICES ? 'nav-active' : ''}`}
              style={{ background: 'none', border: 'none', cursor: 'pointer', outline: 'none', display: 'flex', alignItems: 'center', padding: '0.5rem 0' }}
            >
              Dienstleistungen <i className={`fa-solid fa-chevron-down`} style={{ fontSize: '0.7rem', transition: 'transform 0.3s', transform: dropdownOpen ? 'rotate(180deg)' : 'none' }}></i>
            </button>
            
            <div className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`} onMouseLeave={() => setDropdownOpen(false)}>
              <div className="dropdown-grid">
                {services.map((srv) => (
                  <Link key={srv.id} to={getServiceDetailRoute(srv.id)} className="dropdown-item">
                    <div className="icon-box" style={{ background: `${srv.accentColor}15`, color: srv.accentColor }}>
                      <i className={`fa-solid ${srv.icon}`}></i>
                    </div>
                    <div className="dropdown-content">
                      <span className="title">{srv.title}</span>
                      <p className="dropdown-desc">{srv.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="dropdown-footer">
                <Link to={ROUTES.SERVICES} style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--accent)', textDecoration: 'none' }}>
                  Alle Reparaturdienste ansehen <i className="fa-solid fa-arrow-right"></i>
                </Link>
              </div>
            </div>
          </div>

          <Link to={ROUTES.PREISE} className={location.pathname === ROUTES.PREISE ? 'nav-active' : 'nav-link'}>
            Preise
          </Link>

          <Link to={ROUTES.ABOUT} className={location.pathname === ROUTES.ABOUT ? 'nav-active' : 'nav-link'}>Über uns</Link>
          <Link to={ROUTES.CONTACT} className={location.pathname === ROUTES.CONTACT ? 'nav-active' : 'nav-link'}>Kontakt</Link>
        </nav>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', zIndex: 1100 }}>
          <Link to={ROUTES.SERVICES} className="btn btn-primary hide-mobile" style={{ padding: '0.6rem 1.5rem', fontSize: '0.95rem' }}>Anfrage senden</Link>
          
          <button 
            className="show-mobile" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menü öffnen"
            style={{ 
              width: '44px', height: '44px', borderRadius: '12px', 
              display: 'flex', alignItems: 'center', justifyContent: 'center', 
              background: mobileMenuOpen ? 'var(--primary)' : 'var(--surface-raised)', 
              border: 'none', 
              color: mobileMenuOpen ? 'white' : 'var(--text-main)', 
              fontSize: '1.25rem',
              cursor: 'pointer',
              transition: 'all 0.3s',
              zIndex: 1200
            }}
          >
            <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`} style={{
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100vh',
        background: 'white', zIndex: 1050, padding: '100px 1.5rem 2rem 1.5rem',
        transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
        display: 'flex', flexDirection: 'column', overflowY: 'auto', visibility: mobileMenuOpen ? 'visible' : 'hidden'
      }}>
        <div className="mobile-nav-content">
           <Link to={ROUTES.HOME} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Startseite</Link>
           
           <div className="mobile-services-section">
             <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '1.25rem', letterSpacing: '1px' }}>Dienstleistungen</p>
             <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem' }}>
               {services.map(srv => (
                <Link key={srv.id} to={getServiceDetailRoute(srv.id)} onClick={() => setMobileMenuOpen(false)} style={{ padding: '1rem', borderRadius: '12px', background: 'var(--bg-color)', display: 'flex', alignItems: 'center', gap: '1rem', textDecoration: 'none', border: '1px solid var(--border)' }}>
                   <div style={{ color: srv.accentColor, width: '30px', textAlign: 'center' }}>
                     <i className={`fa-solid ${srv.icon}`}></i>
                   </div>
                   <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-main)' }}>{srv.title}</span>
                 </Link>
               ))}
             </div>
           </div>
           
           <Link to={ROUTES.PREISE} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
             Preise
           </Link>
           <Link to={ROUTES.ABOUT} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Über uns</Link>
           <Link to={ROUTES.CONTACT} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Kontakt</Link>
           <Link to={ROUTES.SERVICES} className="btn btn-primary" onClick={() => setMobileMenuOpen(false)} style={{ width: '100%', marginTop: '2rem', padding: '1.25rem' }}>Reparatur starten</Link>
        </div>
      </div>

      <style>{`
        .nav-link { font-weight: 600; font-size: 1.05rem; color: var(--text-muted); transition: all 0.3s; position: relative; text-decoration: none; }
        .nav-link:hover { color: var(--primary); }
        .nav-active { color: var(--primary); font-weight: 700; text-decoration: none; position: relative; }
        .nav-active::after { content: ''; position: absolute; bottom: -4px; left: 0; width: 100%; height: 2px; background: var(--accent); border-radius: 2px; }

        .dropdown-menu {
          position: absolute;
          top: calc(100% + 15px);
          left: 50%;
          transform: translateX(-50%) translateY(15px);
          width: 820px;
          background: white;
          border-radius: 28px;
          box-shadow: 0 40px 80px rgba(0,0,0,0.14), 0 0 1px rgba(0,0,0,0.1);
          border: 1px solid var(--border);
          opacity: 0;
          visibility: hidden;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          padding: 2rem;
          z-index: 1000;
        }
        .dropdown-menu.show { opacity: 1; visibility: visible; transform: translateX(-50%) translateY(0); }
        .dropdown-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; }
        .dropdown-item { 
          display: flex; 
          flex-direction: column; 
          align-items: flex-start; 
          gap: 1rem; 
          padding: 1.25rem; 
          border-radius: 20px; 
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94); 
          text-decoration: none;
          background: transparent;
          border: 1px solid transparent;
        }
        .dropdown-item:hover { 
          background: var(--surface-raised); 
          border-color: var(--border);
          transform: translateY(-4px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.04);
        }
        .icon-box { 
          width: 44px; 
          height: 44px; 
          border-radius: 12px; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          font-size: 1.1rem; 
          flex-shrink: 0; 
          transition: all 0.3s;
        }
        .dropdown-item:hover .icon-box {
          transform: scale(1.1) rotate(5deg);
        }
        .dropdown-content .title { 
          display: block;
          font-size: 1.05rem; 
          font-weight: 700; 
          color: var(--text-main); 
          margin-bottom: 0.25rem;
        }
        .dropdown-desc {
          font-size: 0.82rem;
          color: var(--text-muted);
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .dropdown-footer { 
          margin-top: 1.5rem; 
          padding-top: 1.25rem; 
          border-top: 1px solid var(--border); 
          text-align: center; 
        }

        .mobile-nav-link { font-size: 1.5rem; font-weight: 700; color: var(--text-main); text-decoration: none; padding: 1.25rem 0; border-bottom: 1px solid var(--border); display: block; }
        .mobile-services-section { padding: 1.5rem 0; border-bottom: 1px solid var(--border); }
      `}</style>
    </header>
  );
};

export default Header;
